<script lang="ts">
  import { onMount } from "svelte";
  import { auth } from "./stores/auth";
  import { api } from "./lib/api";
  import Login from "./views/Login.svelte";
  import Dashboard from "./views/Dashboard.svelte";
  import PodDetail from "./views/PodDetail.svelte";
  import Admin from "./views/Admin.svelte";

  let route = $state(getRoute());
  let checking = $state(true);

  function getRoute() {
    const hash = window.location.hash.slice(1) || "/";
    return hash;
  }

  function navigate(path: string) {
    window.location.hash = path;
  }

  $effect(() => {
    const handler = () => { route = getRoute(); };
    window.addEventListener("hashchange", handler);
    return () => window.removeEventListener("hashchange", handler);
  });

  onMount(async () => {
    try {
      const res = await api.checkAuth();
      if (res.authenticated) {
        auth.set({ authenticated: true, user: res.user ?? null, authType: res.authType ?? null });
        checking = false;
        return;
      }
    } catch {
      // Session not valid
    }

    // Session gone but we have a saved token — auto re-login
    const savedToken = localStorage.getItem("antares_token");
    if (savedToken) {
      try {
        const res = await api.loginWithToken(savedToken);
        if (res.authenticated) {
          auth.set({ authenticated: true, user: res.user ?? null, authType: res.authType ?? "token" });
          checking = false;
          return;
        }
      } catch {
        // Token no longer valid — clear it
        localStorage.removeItem("antares_token");
      }
    }

    checking = false;
  });

  async function handleLogout() {
    try { await api.logout(); } catch { /* ignore */ }
    localStorage.removeItem("antares_token");
    auth.set({ authenticated: false, user: null, authType: null });
    navigate("/login");
  }

  // Parse route params
  let podRoute = $derived(route.match(/^\/pods\/([^/]+)\/([^/]+)$/));
  let podNs = $derived(podRoute?.[1] ?? "");
  let podName = $derived(podRoute?.[2] ?? "");
  let isAdmin = $derived(route === "/admin");
</script>

<div class="app">
  <header class="topbar">
    <button class="logo" onclick={() => navigate("/")}>
      Antares
    </button>
    {#if $auth.authenticated}
      <div class="topbar-right">
        {#if $auth.authType === "token"}
          <button class="btn-ghost" onclick={() => navigate("/admin")}>Admin</button>
        {/if}
        <span class="user">{$auth.user}</span>
        <button class="btn-ghost" onclick={handleLogout}>Logout</button>
      </div>
    {/if}
  </header>

  <main class="content">
    {#if checking}
      <div class="loading">Loading...</div>
    {:else if !$auth.authenticated || route === "/login"}
      <Login />
    {:else if isAdmin && $auth.authType === "token"}
      <Admin />
    {:else if podRoute}
      <PodDetail ns={podNs} name={podName} />
    {:else}
      <Dashboard />
    {/if}
  </main>
</div>

<style>
  .app {
    display: flex;
    flex-direction: column;
    min-height: 100vh;
  }

  .topbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 1rem;
    height: 48px;
    background: var(--bg-surface);
    border-bottom: 1px solid var(--border);
  }

  .logo {
    font-size: 1.1rem;
    font-weight: 700;
    color: var(--accent);
    background: none;
    border: none;
    cursor: pointer;
    letter-spacing: 0.05em;
  }

  .topbar-right {
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }

  .user {
    color: var(--text-muted);
    font-size: 0.85rem;
  }

  .btn-ghost {
    background: none;
    border: 1px solid var(--border);
    color: var(--text-muted);
    padding: 0.25rem 0.75rem;
    border-radius: 4px;
    cursor: pointer;
    font-size: 0.8rem;
  }

  .btn-ghost:hover {
    color: var(--text);
    border-color: var(--text-muted);
  }

  .content {
    flex: 1;
    padding: 1.5rem;
  }

  .loading {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 50vh;
    color: var(--text-muted);
  }
</style>
